import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import StarRating from "@/components/StarRating";
import { CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ReviewForm = ({ orderNumber, restaurantName }) => {
  const [existing, setExisting] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [restaurantRating, setRestaurantRating] = useState(0);
  const [restaurantComment, setRestaurantComment] = useState("");
  const [riderRating, setRiderRating] = useState(0);
  const [riderComment, setRiderComment] = useState("");
  const [appRating, setAppRating] = useState(0);
  const [appComment, setAppComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderNumber}/review`)
      .then((r) => setExisting(r.data && r.data.id ? r.data : null))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [orderNumber]);

  const submit = async () => {
    if (!restaurantRating) { toast.error("Please rate the restaurant"); return; }
    setBusy(true);
    try {
      const payload = {
        restaurant_rating: restaurantRating,
        restaurant_comment: restaurantComment,
        rider_rating: riderRating || null,
        rider_comment: riderComment,
        app_rating: appRating || null,
        app_comment: appComment,
      };
      const r = await api.post(`/orders/${orderNumber}/review`, payload);
      setExisting(r.data);
      toast.success("Thanks for your feedback! 🌴");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review");
    } finally { setBusy(false); }
  };

  if (!loaded) return null;

  if (existing) {
    return (
      <div className="mt-6 bg-teal/10 rounded-2xl border border-teal/30 p-5 flex items-center gap-3" data-testid="review-already-submitted">
        <CheckCircle2 className="w-6 h-6 text-teal shrink-0" />
        <div>
          <div className="font-heading font-bold text-navy">Thanks for your review!</div>
          <div className="text-sm text-navy/70">You rated {restaurantName} {existing.restaurant_rating}/5. Your feedback helps other travellers.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 bg-white rounded-2xl border border-navy/10 p-6" data-testid="review-form">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-coral" />
        <h2 className="font-heading text-xl font-extrabold text-navy">Rate your experience</h2>
      </div>
      <p className="text-sm text-navy/60 mt-1">A few taps — help other holidaymakers pick the best food.</p>

      <div className="mt-5 space-y-5">
        <div>
          <Label className="font-heading font-bold text-navy">Restaurant — {restaurantName} <span className="text-coral">*</span></Label>
          <StarRating value={restaurantRating} onChange={setRestaurantRating} testid="rating-restaurant" />
          <Textarea
            value={restaurantComment}
            onChange={(e) => setRestaurantComment(e.target.value)}
            placeholder="How was the food?"
            className="mt-2"
            data-testid="comment-restaurant"
          />
        </div>

        <div>
          <Label className="font-heading font-bold text-navy">Rider</Label>
          <StarRating value={riderRating} onChange={setRiderRating} testid="rating-rider" />
          <Textarea
            value={riderComment}
            onChange={(e) => setRiderComment(e.target.value)}
            placeholder="Did the rider arrive on time and treat you well?"
            className="mt-2"
            data-testid="comment-rider"
          />
        </div>

        <div>
          <Label className="font-heading font-bold text-navy">Caleta Eats app</Label>
          <StarRating value={appRating} onChange={setAppRating} testid="rating-app" />
          <Textarea
            value={appComment}
            onChange={(e) => setAppComment(e.target.value)}
            placeholder="Was the app easy to use?"
            className="mt-2"
            data-testid="comment-app"
          />
        </div>

        <Button onClick={submit} disabled={busy} className="btn-coral w-full h-12" data-testid="submit-review-btn">
          {busy ? "Sending…" : "Submit review"}
        </Button>
      </div>
    </div>
  );
};

export default ReviewForm;
